/**
 * The strip above the canvas: which document you are looking at, a way across
 * to the other one, and the version picker.
 *
 * Selection lives in the URL (`?version=5`, absent for the draft) so the back
 * button walks the history and a link to a particular version can be shared.
 */

import { Button, Select, Tooltip } from 'antd';
import {
	ExclamationCircleFilled,
	FullscreenExitOutlined,
	FullscreenOutlined,
	LoadingOutlined,
} from '@ant-design/icons';
import React, { FC } from 'react';
import { Link } from 'react-router-dom';
import DateTimeHover from '../date_time_component/DateTime';
import { UserNameWithEmail } from '../name_component/user/UserNameWithEmail';
import { AOPVersionSummary } from '../../types/AOPTypes';
import { AOPVersionMode } from './utils/AOPVersionUtils';

/**
 * `saving` covers both the pause and a request in flight: to the author they
 * are the same wait. `failed` means the edit is on the canvas and the service
 * has not taken it. `stale` means a read failed, so what is shown may be
 * behind.
 */
export type AOPSaveStatus = 'saving' | 'failed' | 'stale' | null;

const SAVE_STATUS_TEXT: { [status: string]: string } = {
	saving: 'Saving…',
	failed: 'Not saved. Your last change is still here but the service has not taken it.',
	stale: 'Could not refresh. What is shown may be behind what the service holds.',
};

/**
 * The save, as an icon with the sentence on hover.
 *
 * An icon because the strip has room for one and the canvas does not, and
 * because this is a status rather than something to act on. The text is on the
 * element as well as in the tooltip: a tooltip is a hover, and somebody reading
 * the page with a screen reader never gets one.
 */
const SaveStatus: FC<{ status: AOPSaveStatus }> = ({ status }) => {
	if (!status) {
		return null;
	}

	const failed = status !== 'saving';

	return (
		/*
		  Reachable without a mouse. A tooltip on its own is a hover, so a
		  keyboard or touch user saw a red mark and had no way to find out their
		  edit had not been saved — focus and tap open it too, and the icon takes
		  focus so there is something to reach.
		*/
		<Tooltip title={SAVE_STATUS_TEXT[status]} trigger={['hover', 'focus', 'click']}>
			<span
				role="status"
				tabIndex={0}
				aria-label={SAVE_STATUS_TEXT[status]}
				className={`inline-flex shrink-0 cursor-help items-center ${
					failed ? 'text-character-danger' : 'text-character-secondary'
				}`}
			>
				{failed ? <ExclamationCircleFilled /> : <LoadingOutlined />}
			</span>
		</Tooltip>
	);
};

interface AOPVersionBarProps {
	mode: AOPVersionMode;
	selectedVersion: number | null;
	publishedVersion: number | null;
	summaries: AOPVersionSummary[];
	/** How many versions there are. The picker holds a page of them. */
	versionCount?: number;
	isLoadingMoreVersions: boolean;
	onLoadMoreVersions: () => void;
	/** The draft holds changes that are not in the latest published version. */
	/**
	 * Whether the draft holds anything the live version does not — or null
	 * while that cannot be answered, because the live version's document has
	 * not been read yet or could not be.
	 */
	hasUnpublishedChanges: boolean | null;
	/** Route for a version, or for the draft when given null. */
	hrefFor: (version: number | null) => string;
	/** Who last touched the draft, and when. From the procedure, not a version. */
	draftUpdatedAt: string | null;
	draftUpdatedBy: string | null;
	onSelectVersion: (version: number | null) => void;
	/**
	 * What the canvas's last save did, if it is worth saying.
	 *
	 * Shown here rather than over the canvas: it used to be a sentence floating
	 * at the canvas's top-right corner, in the slot that otherwise holds a quiet
	 * "Saving…", and a twelve-word sentence in a one-word slot read as an error
	 * banner laid over the author's work.
	 */
	saveStatus: AOPSaveStatus;
	/** The canvas is filling the window rather than sitting in the page. */
	isFullscreen: boolean;
	onToggleFullscreen: () => void;
	/**
	 * Where the picker's list is portalled. The default, `document.body`, is
	 * wrong once the canvas is a fixed overlay, since the body is outside it.
	 */
	popupContainer?: (trigger: HTMLElement) => HTMLElement;
}

const DRAFT_OPTION = 'draft';

/**
 * One line in the picker: what the document is, who put it there and when, and
 * whatever they said about it.
 *
 * The person is drawn from their email — the only thing a version carries —
 * through `UserNameWithEmail`, which resolves and caches the contact and
 * batches the lookups, so a list of versions published by the same two people
 * costs one request rather than one per row.
 */
const VersionRow: FC<{
	name: string;
	status: string;
	email: string | null;
	at: string | null;
	verb: string;
	note: string | null;
}> = ({ name, status, email, at, verb, note }) => {
	return (
		<div className="flex flex-col gap-2xs py-2xs">
			<div className="flex items-center gap-xs">
				<span className="bodymedium shrink-0 text-character-primary">{name}</span>
				<span className="footnoteregular shrink-0 text-character-secondary">{status}</span>
				<span className="flex-1" />
				{email ? (
					<span className="footnoteregular inline-flex items-center gap-2xs text-character-secondary">
						{/* "(You)" is appended by `UserName` itself, for every screen. */}
						<UserNameWithEmail mode="inline" size="small" showNameWithAvatar email={email} />
						<span>· {verb}</span>
						{/*
						  Times are stamped UTC and read by people in other places, so the
						  zone travels with them. `DateTimeHover` falls back to the
						  viewer's own zone, prints its abbreviation, and gives the
						  relative time on hover.
						*/}
						<DateTimeHover timestamp={at} />
					</span>
				) : null}
			</div>
			{note ? <span className="bodyregular text-character-primary">{note}</span> : null}
		</div>
	);
};

export const AOPVersionBar: FC<AOPVersionBarProps> = ({
	mode,
	selectedVersion,
	publishedVersion,
	summaries,
	versionCount,
	isLoadingMoreVersions,
	onLoadMoreVersions,
	hasUnpublishedChanges,
	hrefFor,
	draftUpdatedAt,
	draftUpdatedBy,
	onSelectVersion,
	saveStatus,
	isFullscreen,
	onToggleFullscreen,
	popupContainer,
}) => {
	const message = (): React.ReactNode => {
		if (mode !== 'draft') {
			return hasUnpublishedChanges ? (
				<>
					A new draft version exists and is not yet published.{' '}
					<Link to={hrefFor(null)}>View draft version</Link>
				</>
			) : null;
		}

		if (publishedVersion === null || publishedVersion === undefined) {
			return 'This procedure has never been published.';
		}

		/*
		  Saying nothing is the honest answer while the comparison cannot be made.
		  "This draft matches published vN" is a claim about an author's unsaved
		  work, and it was being made on the strength of a document that had not
		  loaded — so an edit nobody had published could be announced as live.
		*/
		if (hasUnpublishedChanges === null) {
			return <Link to={hrefFor(publishedVersion)}>View published version</Link>;
		}

		return hasUnpublishedChanges ? (
			<>
				Your changes are saved as a draft and are not published yet.{' '}
				<Link to={hrefFor(publishedVersion)}>View published version</Link>
			</>
		) : (
			<>
				This draft matches published v{publishedVersion}.{' '}
				<Link to={hrefFor(publishedVersion)}>View published version</Link>
			</>
		);
	};

	// Whether the version being shown has a row in the list. One reached by a
	// link can be older than the page held, in which case the picker has no
	// option for it until more are loaded.
	const hasOptionForSelected =
		selectedVersion === null || summaries.some((summary) => summary.version === selectedVersion);

	// `count` is every version, and the list holds a page of them.
	const hasMoreVersions = typeof versionCount === 'number' && summaries.length < versionCount;

	return (
		// No top padding: the bar sits directly under the page header, so the gap
		// above it is that header's, not this one's.
		<div className="aop-version-bar flex shrink-0 items-center gap-xs bg-grey-1 px-s pb-xs pt-0">
			<span className="bodyregular text-character-primary">{message()}</span>
			<span className="flex-1" />
			<SaveStatus status={saveStatus} />
			<Select
				// antd 4 caches the label of the selected option and only refreshes it
				// when the *value* changes. The draft option's label depends on whether
				// anything has been published, which it learns after the first render —
				// so without remounting, the picker kept saying "Draft (not published)"
				// beside a bar offering to show the published version.
				/*
				  Not keyed on how many are held — the history pages, and remounting
				  on every page would shut the dropdown the moment somebody asked for
				  more. What the cached label actually depends on is whether the
				  selected version is among the options, so that is what is keyed.
				*/
				key={`versions:${publishedVersion}:${hasOptionForSelected}`}
				aria-label="Version"
				size="small"
				className="w-[200px]"
				// The trigger stays the width of the bar's corner while the list is as
				// wide as the sentences in it.
				optionLabelProp="label"
				dropdownMatchSelectWidth={false}
				dropdownStyle={{ width: 560 }}
				getPopupContainer={popupContainer}
				/*
				  The footer sits below the options rather than among them: a row
				  inside the list would be selectable, and choosing "load more" as if
				  it were a version is the sort of thing a picker should not allow.
				  `onMouseDown` is swallowed so pressing the button does not take the
				  focus that closes the dropdown.
				*/
				dropdownRender={(menu) => (
					<>
						{menu}
						{hasMoreVersions ? (
							<div
								className="flex items-center justify-between gap-s border-0 border-t border-solid border-grey-4 px-s py-xs"
								onMouseDown={(event) => event.preventDefault()}
							>
								<span className="footnoteregular text-character-secondary">
									Showing {summaries.length} of {versionCount}
								</span>
								<Button size="small" loading={isLoadingMoreVersions} onClick={onLoadMoreVersions}>
									Load more
								</Button>
							</div>
						) : null}
					</>
				)}
				value={selectedVersion === null ? DRAFT_OPTION : String(selectedVersion)}
				onChange={(value) =>
					onSelectVersion(value === DRAFT_OPTION ? null : parseInt(value as string, 10))
				}
			>
				<Select.Option
					value={DRAFT_OPTION}
					label={
						publishedVersion === null || publishedVersion === undefined
							? 'Draft (not published)'
							: 'Draft'
					}
				>
					<VersionRow
						name="Draft"
						// The same three-state answer the bar beside it uses. Saying
						// "Unpublished changes" for anything ever published contradicted
						// that bar on a draft identical to the live version.
						status={
							publishedVersion === null || publishedVersion === undefined
								? 'Not published'
								: hasUnpublishedChanges === null
								? 'Draft'
								: hasUnpublishedChanges
								? 'Unpublished changes'
								: 'Matches the live version'
						}
						email={draftUpdatedBy}
						verb="Last edited"
						at={draftUpdatedAt}
						// A draft has no change note: the note is written when a version is
						// cut from it, which has not happened yet.
						note={null}
					/>
				</Select.Option>
				{summaries.map((summary) => (
					<Select.Option
						key={summary.version}
						value={String(summary.version)}
						label={`v${summary.version}${summary.version === publishedVersion ? ' · live' : ''}`}
					>
						<VersionRow
							name={`v${summary.version}`}
							status={
								summary.version === publishedVersion
									? 'Currently published'
									: 'Previously published'
							}
							email={summary.published_by}
							verb="Published"
							at={summary.published_at}
							/*
							  The change note doubles as the version's name: it is the only
							  thing a version carries that says what it is.
							*/
							note={summary.change_note}
						/>
					</Select.Option>
				))}
			</Select>
			{/*
			  Fitting the procedure to the screen already has a button, in the zoom
			  cluster on the canvas itself. This one gives the canvas the whole
			  window, which is the thing the bar cannot do from anywhere else.
			*/}
			<Button
				size="small"
				className="aop-sentence-case"
				icon={isFullscreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />}
				onClick={onToggleFullscreen}
			>
				{isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
			</Button>
		</div>
	);
};

export default AOPVersionBar;
